import { Badge } from "@/components/ui/badge";

/**
 * Renders a palette-mapped Badge for any known record status.
 * Central source of truth so every page uses the same visual vocabulary
 * for application / school / campaign / donation status.
 *
 * Unknown statuses fall back to the `outline` variant so we never break
 * the layout on unexpected data.
 */
const STATUS_VARIANTS = {
  // School applications
  pending:  "warning",
  approved: "success",
  rejected: "destructive",

  // Schools
  active:   "success",
  archived: "secondary",

  // Campaigns
  draft:    "outline",
  early_donations: "default",
  grace:    "warning",
  locked:   "secondary",

  // Donations
  paid:     "success",
  failed:   "destructive",
  refunded: "outline",
};

// What people see. Internal state names ("grace", "locked") stay in code.
export const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  active: "Live",
  archived: "Archived",
  draft: "Getting ready",
  // A draft already taking donations before reading starts (Campaign#display_state).
  early_donations: "Donations open",
  grace: "Wrapping up",
  locked: "Finished",
  paid: "Paid",
  failed: "Failed",
  refunded: "Refunded",
};

export default function StatusBadge({ status, className = "", showActive = false }) {
  // Active is the regular expected state — hide unless the caller opts in
  // (e.g. campaign pickers / history lists where it disambiguates).
  if (status === "active" && !showActive) return null;
  const variant = STATUS_VARIANTS[status] || "outline";
  return (
    <Badge variant={variant} className={className}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}
